/**
 * Inputs — the workflow that makes insufficiency actionable (IA spec §4).
 *
 * One row per outstanding INPUT, never per requirement: one attestation may
 * unlock four parts, and a user shown the same missing item four times reads
 * the system as broken. Ordered by unlock value — the first row is the
 * highest-leverage action.
 *
 * Rules pinned by tests:
 *  - dismissal REQUIRES a reason (the action is disabled without one), and a
 *    dismissed row stays visible carrying that reason — never hidden;
 *  - supplying an input never changes a determination here: the row shows
 *    "Recorded. Determinations update on the next assessment run." until a
 *    re-assessment runs (the only determination path is the engine);
 *  - copy frames rows as available actions, never deficiencies.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api, { errorDetail } from '../services/api';
import RunAssessmentModal from '../components/compliance/RunAssessmentModal';

const STATUS_STYLES = {
  outstanding: 'bg-amber-900/40 text-amber-300 border-amber-700',
  supplied: 'bg-green-900/40 text-green-300 border-green-700',
  expiring: 'bg-orange-900/40 text-orange-300 border-orange-700',
  expired: 'bg-red-900/40 text-red-300 border-red-700',
  dismissed: 'bg-gray-700/40 text-gray-400 border-gray-600',
};

function DismissModal({ inputKey, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/skills/compliance/inputs/${inputKey}/dismiss`, { reason });
      onDone();
    } catch (err) {
      setError(errorDetail(err, 'The dismissal was not recorded.'));
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" data-testid="dismiss-modal">
      <div className="bg-gray-800 border border-gray-600 rounded-xl p-6 w-full max-w-lg">
        <h3 className="text-white font-semibold mb-1">Dismiss this input</h3>
        <p className="text-xs text-gray-400 mb-3">
          "Not applicable to us" is a legitimate answer; an unrecorded disappearance is not.
          The reason is recorded and stays retrievable.
        </p>
        <textarea
          data-testid="dismiss-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Why this input does not apply to this deployment…"
          className="w-full h-24 bg-gray-900 border border-gray-600 rounded-lg p-2 text-sm text-gray-200"
        />
        {error && <div className="text-red-300 text-xs mt-2">{error}</div>}
        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-3 py-1.5 text-sm rounded-lg border border-gray-600 text-gray-300">
            Cancel
          </button>
          <button
            data-testid="dismiss-confirm"
            onClick={submit}
            disabled={!reason.trim() || busy}
            className="px-3 py-1.5 text-sm rounded-lg bg-gray-600 text-white disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Record dismissal
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ComplianceInputs() {
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissing, setDismissing] = useState(null);
  const [running, setRunning] = useState(false);
  const [notice, setNotice] = useState(null);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const load = useCallback(async () => {
    setError(null);
    try {
      const { data } = await api.get('/skills/compliance/inputs');
      if (!mounted.current) return;
      setDoc(data);
    } catch (err) {
      if (!mounted.current) return;
      setError(errorDetail(err, 'Inputs could not be loaded.'));
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const revoke = async (inputKey) => {
    await api.post(`/skills/compliance/inputs/${inputKey}/revoke-dismissal`);
    load();
  };

  const anyAwaiting = (doc?.rows || []).some((r) => r.awaiting_reassessment);


  return (
    <div className="bg-gray-900 text-gray-100 min-h-screen">
      <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold text-white mb-1">Inputs</h1>
      <div className="flex items-start justify-between">
        <p className="text-sm text-gray-400 mb-5">
          What supplying each input concludes, and who supplies it. The highest-leverage action is first.
        </p>
        <button data-testid="run-assessment" onClick={() => setRunning(true)}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-800 whitespace-nowrap">
          Run assessment
        </button>
      </div>

      {loading && <div className="text-gray-400 text-sm">Loading inputs…</div>}
      {error && (
        <div data-testid="inputs-error" className="bg-red-900/30 border border-red-800 rounded-xl p-5 text-sm text-red-200">
          {error}
          <button onClick={load} className="ml-3 underline">Retry</button>
        </div>
      )}
      {notice && <div className="bg-gray-800 border border-gray-600 rounded-lg p-3 text-sm text-gray-200 mb-4">{notice}</div>}

      {!loading && !error && doc && (
        <>
          {anyAwaiting && (
            <div data-testid="awaiting-banner" className="bg-blue-900/30 border border-blue-800 rounded-xl p-4 mb-4 text-sm text-blue-200 flex items-center justify-between">
              <span>
                Recorded. Determinations update on the next assessment run.
              </span>
              <button
                data-testid="reassess-now"
                onClick={() => setRunning(true)}
                className="px-3 py-1.5 rounded-lg border border-blue-600 hover:bg-blue-900/40"
              >
                Run assessment
              </button>
            </div>
          )}
          <div className="space-y-3">
            {doc.rows.map((r) => (
              <div key={r.input_key} data-testid={`input-${r.input_key}`}
                   className="bg-gray-800 rounded-xl border border-gray-700 p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-white">{r.what_is_needed}</div>
                    <div className="text-xs text-gray-400 mt-1">
                      Supplied by: {r.who_supplies} · {r.how_supplied}
                    </div>
                    {r.detail && <div className="text-xs text-gray-400 mt-1">{r.detail}</div>}
                    {r.unlock_count > 0 && (
                      <div className="text-xs text-gray-300 mt-2">
                        Supplying this concludes {r.unlock_count} requirement part{r.unlock_count === 1 ? '' : 's'}:{' '}
                        {r.unlocks.map((u) => u.part).join(', ')}
                      </div>
                    )}
                    {r.status === 'dismissed' && r.dismissal && (
                      <div data-testid={`dismissal-reason-${r.input_key}`} className="text-xs text-gray-400 mt-2 border-l-2 border-gray-600 pl-2">
                        Dismissed by {r.dismissal.dismissed_by}: “{r.dismissal.reason}”
                      </div>
                    )}
                    {r.awaiting_reassessment && (
                      <div className="text-xs text-blue-300 mt-2">
                        Recorded. Determinations update on the next assessment run.
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className={`inline-block px-2 py-0.5 text-[11px] rounded-full border whitespace-nowrap ${STATUS_STYLES[r.status] || STATUS_STYLES.outstanding}`}>
                      {r.status}
                    </span>
                    {(r.status === 'outstanding' || r.status === 'expired' || r.status === 'expiring') && (
                      <>
                        {r.how_supplied.includes('not an in-app form') ? null : (
                          <Link to={`/compliance/scope-facts?kind=${r.input_key}`}
                                className="text-xs text-blue-300 underline">
                            Supply
                          </Link>
                        )}
                        <button onClick={() => setDismissing(r.input_key)}
                                data-testid={`dismiss-${r.input_key}`}
                                className="text-xs text-gray-400 underline">
                          Dismiss with reason
                        </button>
                      </>
                    )}
                    {r.status === 'dismissed' && (
                      <button onClick={() => revoke(r.input_key)} className="text-xs text-gray-400 underline">
                        Revoke dismissal
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      {dismissing && (
        <DismissModal inputKey={dismissing} onClose={() => setDismissing(null)}
                      onDone={() => { setDismissing(null); load(); }} />
      )}
      {running && (
        <RunAssessmentModal onClose={() => setRunning(false)}
                            onDone={() => load()} />
      )}
    </div>
    </div>
  );
}
